/**
 * Control settings: the movement scheme (input/controls.ts). Persisted in localStorage like the
 * combat audio / text settings; every storage access is in try/catch. `controlSettings` is a tiny
 * shared store: the settings panel writes it, running sessions read it live.
 */
import { el } from '../hud/dom';
import { CONTROL_SCHEMES, type ControlScheme } from './controls';

export interface ControlSettings {
  /**
   * 'action' (default): the hero keeps its facing, A/D strafe, right-drag steers. 'wow': classic
   * WoW controls (A/D turn; right mouse strafes). 'camera': camera-relative WASD, face the run.
   */
  scheme: ControlScheme;
  /** +controls: KeyboardEvent.code of the optional turn-left / turn-right keys (null = unbound, the default). */
  turnLeftKey: string | null;
  turnRightKey: string | null;
  /** +autoattack: KeyboardEvent.code of the Attack key (toggle auto-attack), null = unbound (default, as in WoW). */
  attackKey: string | null;
}

/** +autoattack: keys the Attack bind may take (none is a game, menu or hub key). */
export const ATTACK_KEY_CHOICES: readonly { code: string | null; label: string }[] = [
  { code: null, label: 'Unbound' },
  { code: 'Backquote', label: '`' },
  { code: 'KeyI', label: 'I' },
  // +prof: L opens the Professions panel now (a save with Attack on L falls back to unbound).
  // +quests: Y opens the quest log now (a save with Attack on Y falls back to unbound).
];

/** +controls: keys the optional turn binds may take (an arrow replaces its strafe; Q / E replace that ability key). */
export const TURN_LEFT_CHOICES: readonly { code: string | null; label: string }[] = [
  { code: null, label: 'Unbound' },
  { code: 'ArrowLeft', label: '←' },
  { code: 'KeyQ', label: 'Q' },
];
export const TURN_RIGHT_CHOICES: readonly { code: string | null; label: string }[] = [
  { code: null, label: 'Unbound' },
  { code: 'ArrowRight', label: '→' },
  { code: 'KeyE', label: 'E' },
];

/** Scheme labels for the settings select. */
export const SCHEME_LABELS: Readonly<Record<ControlScheme, string>> = {
  action: 'Action (A/D strafe, right-drag steers)',
  wow: 'Classic WoW (A/D turn)',
  camera: 'Camera-relative (face where you run)',
};

export const DEFAULT_CONTROLS: Readonly<ControlSettings> = Object.freeze({ scheme: 'action', attackKey: null, turnLeftKey: null, turnRightKey: null });
/** v2: 'action' became the default scheme. */
export const CONTROLS_KEY = 'keystone.controls.v2';
/** v1 saves (default 'camera') migrate once: 'camera' → 'action', an explicit 'wow' stays. */
export const CONTROLS_KEY_V1 = 'keystone.controls.v1';

const pick = (choices: readonly { code: string | null }[], v: unknown): string | null => (choices.some((c) => c.code !== null && c.code === v) ? (v as string) : null);

export function sanitizeControls(raw: unknown): ControlSettings {
  const s: ControlSettings = { ...DEFAULT_CONTROLS };
  if (!raw || typeof raw !== 'object') return s;
  const r = raw as Record<string, unknown>;
  if ((CONTROL_SCHEMES as readonly unknown[]).includes(r['scheme'])) s.scheme = r['scheme'] as ControlScheme;
  s.attackKey = pick(ATTACK_KEY_CHOICES, r['attackKey']);
  s.turnLeftKey = pick(TURN_LEFT_CHOICES, r['turnLeftKey']);
  s.turnRightKey = pick(TURN_RIGHT_CHOICES, r['turnRightKey']);
  return s;
}

/** A v1 save → v2 settings: the old default 'camera' moves to 'action'; 'wow' and the Attack key carry over. */
export function migrateControlsV1(raw: unknown): ControlSettings {
  const s = sanitizeControls(raw);
  if (s.scheme === 'camera') s.scheme = 'action';
  return s;
}

function defaultStorage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

export function loadControls(storage: Pick<Storage, 'getItem'> | null = defaultStorage()): ControlSettings {
  try {
    const raw = storage?.getItem(CONTROLS_KEY);
    if (raw) return sanitizeControls(JSON.parse(raw));
    const old = storage?.getItem(CONTROLS_KEY_V1);
    return old ? migrateControlsV1(JSON.parse(old)) : { ...DEFAULT_CONTROLS };
  } catch {
    return { ...DEFAULT_CONTROLS };
  }
}

export function saveControls(s: ControlSettings, storage: Pick<Storage, 'setItem'> | null = defaultStorage()): boolean {
  try {
    if (!storage) return false;
    storage.setItem(CONTROLS_KEY, JSON.stringify(sanitizeControls(s)));
    return true;
  } catch {
    return false;
  }
}

type Listener = (s: Readonly<ControlSettings>) => void;

class ControlStore {
  private value: ControlSettings | null = null;
  private readonly listeners = new Set<Listener>();

  get(): Readonly<ControlSettings> {
    return (this.value ??= loadControls());
  }

  set(patch: Partial<ControlSettings>): void {
    this.value = sanitizeControls({ ...this.get(), ...patch });
    saveControls(this.value);
    for (const l of this.listeners) l(this.value);
  }

  /** Calls `fn` now and on every change. Returns an unsubscribe function. */
  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    fn(this.get());
    return () => this.listeners.delete(fn);
  }
}

export const controlSettings = new ControlStore();

/** Adds a "Controls" section to the settings panel. Returns a sync function. */
export function mountControlsControls(panel: HTMLElement, before: Node | null = null): () => void {
  const section = el('div', 'ctl-settings');
  panel.insertBefore(section, before);
  el('div', 'gfx-title', section, 'Controls');
  const row = el('label', 'gfx-row', section);
  el('span', '', row, 'Movement');
  const scheme = el('select', '', row);
  for (const k of CONTROL_SCHEMES) {
    const o = el('option', '', scheme, SCHEME_LABELS[k]);
    o.value = k;
  }
  scheme.addEventListener('change', () => controlSettings.set({ scheme: scheme.value as ControlScheme }));
  const hint = el('div', 'gfx-hint', section);
  const hintFor = (k: ControlScheme): string =>
    k === 'action'
      ? 'You keep facing forward: W/S run and backpedal (slower), A/D strafe. Hold right mouse and drag to turn the camera and the hero together; left drag only looks around; both buttons run. NumLock / Mouse 4: autorun.'
      : k === 'wow'
        ? 'A/D turn and S backpedals (slower). Hold right mouse to steer: then A/D strafe and both buttons run. NumLock / Mouse 4: autorun.'
        : 'WASD move relative to the camera and the hero faces where it runs. Drag either button to look around. NumLock / Mouse 4: autorun.';
  scheme.addEventListener('change', () => (hint.textContent = hintFor(scheme.value as ControlScheme)));
  // +controls: optional keyboard turning (unbound by default; Q / E are ability keys).
  const turnSel = (label: string, choices: readonly { code: string | null; label: string }[], key: 'turnLeftKey' | 'turnRightKey'): HTMLSelectElement => {
    const r = el('label', 'gfx-row', section);
    el('span', '', r, label);
    const sel = el('select', '', r);
    for (const c of choices) {
      const o = el('option', '', sel, c.label);
      o.value = c.code ?? '';
    }
    sel.addEventListener('change', () => controlSettings.set({ [key]: sel.value || null }));
    return sel;
  };
  const tl = turnSel('Turn left', TURN_LEFT_CHOICES, 'turnLeftKey');
  const tr = turnSel('Turn right', TURN_RIGHT_CHOICES, 'turnRightKey');
  el('div', 'gfx-hint', section, 'Optional keyboard turning (action / WoW controls). A turn key replaces what it was bound to.');
  // +autoattack: the Attack key (WoW "Attack"); right-click on an enemy or a weapon ability also starts it.
  const atk = el('label', 'gfx-row', section);
  el('span', '', atk, 'Attack key');
  const sel = el('select', '', atk);
  for (const c of ATTACK_KEY_CHOICES) {
    const o = el('option', '', sel, c.label);
    o.value = c.code ?? '';
  }
  sel.addEventListener('change', () => controlSettings.set({ attackKey: sel.value || null }));
  el('div', 'gfx-hint', section, 'Toggles auto-attack. Right-click an enemy or use a weapon ability to start it too.');
  return () => {
    const c = controlSettings.get();
    scheme.value = c.scheme;
    hint.textContent = hintFor(c.scheme);
    tl.value = c.turnLeftKey ?? '';
    tr.value = c.turnRightKey ?? '';
    sel.value = controlSettings.get().attackKey ?? '';
  };
}
